import type { DesktopOfflineRefusal, DesktopOfflineSecret } from '@dob/domain';
import { formatDateTime } from '@dob/i18n';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound, WifiOff } from 'lucide-react';
import { useState, type SyntheticEvent } from 'react';

import { Alert, Button, TextInput } from '../../components/ui.tsx';
import { useLang } from '../../i18n.ts';
import { desktop } from '../../platform.ts';

/**
 * Dob Desktop only (M7-5, plan §7.4): signing in on this PC without internet, with the password
 * or the PIN. The main process checks it (on this PC's own copy, or through the location's hub)
 * and opens a limited offline session (the O3 allow-list); the page only learns the outcome.
 */
export function OfflineSignInForm({ onCancel }: { readonly onCancel?: () => void }) {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [secret, setSecret] = useState<DesktopOfflineSecret>('PIN');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState<'identifier' | 'value' | null>(null);
  const [refusal, setRefusal] = useState<{
    reason: DesktopOfflineRefusal;
    lockedUntil: string | null;
  } | null>(null);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    if (!desktop) return;
    if (identifier.trim() === '' || value === '') {
      setMissing(identifier.trim() === '' ? 'identifier' : 'value');
      return;
    }
    setMissing(null);
    setBusy(true);
    setRefusal(null);
    try {
      const result = await desktop.offline.signIn({ identifier, secret, value });
      if (result.ok) {
        setValue('');
        void navigate({ to: '/offline' });
        return;
      }
      setRefusal({ reason: result.reason, lockedUntil: result.lockedUntil });
      setValue('');
    } finally {
      setBusy(false);
    }
  };

  const message = refusal
    ? t(`offline.r_${refusal.reason}` as const, {
        time: refusal.lockedUntil ? formatDateTime(refusal.lockedUntil, lang) : '',
      })
    : null;

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="flex flex-col gap-4"
      noValidate
      data-offline-signin
    >
      <Alert tone="info">
        <span className="inline-flex items-center gap-2">
          <WifiOff className="size-4 shrink-0" aria-hidden />
          {t('offline.intro')}
        </span>
      </Alert>
      {message ? <Alert tone="error">{message}</Alert> : null}
      <TextInput
        label={t('offline.identifier')}
        hint={t('offline.identifierHint')}
        name="offline-identifier"
        autoComplete="username"
        inputMode="tel"
        dir="ltr"
        value={identifier}
        error={missing === 'identifier' ? t('offline.required') : null}
        onChange={(e) => {
          setIdentifier(e.target.value);
          setMissing(null);
        }}
        className="text-start"
      />
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-sm font-semibold">{t('offline.method')}</legend>
        <div className="grid grid-cols-2 gap-2">
          {(['PIN', 'PASSWORD'] as const).map((kind) => (
            <label
              key={kind}
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border p-3 text-sm font-semibold has-[:checked]:border-brand-red"
            >
              <input
                type="radio"
                name="offline-secret"
                value={kind}
                checked={secret === kind}
                onChange={() => {
                  setSecret(kind);
                  setValue('');
                  setRefusal(null);
                }}
                className="size-4 accent-[var(--dob-primary)]"
              />
              {kind === 'PIN' ? t('offline.pin') : t('offline.password')}
            </label>
          ))}
        </div>
      </fieldset>
      <TextInput
        label={secret === 'PIN' ? t('offline.pin') : t('offline.password')}
        type="password"
        name="offline-secret-value"
        autoComplete={secret === 'PIN' ? 'off' : 'current-password'}
        inputMode={secret === 'PIN' ? 'numeric' : 'text'}
        dir="ltr"
        value={value}
        error={missing === 'value' ? t('offline.required') : null}
        onChange={(e) => {
          setValue(e.target.value);
          setMissing(null);
        }}
        className={secret === 'PIN' ? 'num text-center text-2xl tracking-[0.5em]' : 'text-start'}
      />
      <Button type="submit" variant="primary" size="lg" icon={KeyRound} loading={busy}>
        {busy ? t('offline.checking') : t('offline.submit')}
      </Button>
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-semibold text-link underline-offset-4 hover:underline"
        >
          {t('offline.backToOnline')}
        </button>
      ) : null}
    </form>
  );
}
